import { FC, useEffect, useState } from 'react';
import { Control, FieldPath, useWatch } from 'react-hook-form';

import FormWarning from 'dashboard/components/FormControls/FormWarning';
import { IconAlertCircleStyled } from 'dashboard/components/FormControls/FormWarning/FormWarning';
import { ThemeSetAttributes } from 'dashboard/models/theme-set';
import {
  getFieldLabel,
  getValueFromObject,
} from 'dashboard/views/DashboardThemeSetsEditorNewView/util';
import { Paragraph } from 'design-system/components/Text';
import { isColorContrastHighEnough } from 'lib/utils/formValidation';

const createPostfix = (texts: string[]) =>
  texts.length === 1
    ? `${texts[0]}`
    : texts.length === 2
      ? `${texts[0]} and ${texts[1]}`
      : `${texts.slice(0, texts.length - 1).join(', ')}, and ${texts[texts.length - 1]}`;

interface WCAGWarningProps {
  /** Hex color value to compare against (e.g. #FFFFFF) */
  hexColor: string;
  /** List of fields to check for contrast with the hexColor */
  checkComplianceAgainst: FieldPath<ThemeSetAttributes>[];
  control: Control<ThemeSetAttributes, object>;
}

/**
 * Displays a warning message if there is not enough contrast between the provided hex color and any
 * of the given fields (checkComplianceAgainst). Contrast is checked according to the WCAG guidelines.
 * The warning message will appear/disappear whenever the hex color or the fields' values change.
 * NOTE: This component only works within the theme set editor form.
 */
const WCAGWarning: FC<WCAGWarningProps> = ({ hexColor, checkComplianceAgainst, control }) => {
  const [conflictingAttributes, setConflictingAttributes] = useState<
    FieldPath<ThemeSetAttributes>[]
  >([]);
  const conflictingAttributeLabels = conflictingAttributes.flatMap((attribute) => {
    const fieldLabel = getFieldLabel(attribute);
    return fieldLabel ? [fieldLabel] : [];
  });

  const watchedAttributes = useWatch({ name: checkComplianceAgainst, control });

  useEffect(() => {
    const updatedConflictingAttributes = watchedAttributes.flatMap(
      (attributeValue, attributeIndex) => {
        const attributeHexValue = getValueFromObject(attributeValue);
        const isConflicting =
          attributeHexValue && hexColor && !isColorContrastHighEnough(attributeHexValue)(hexColor);
        return isConflicting ? [checkComplianceAgainst[attributeIndex]] : [];
      },
    );
    setConflictingAttributes(updatedConflictingAttributes);
  }, [checkComplianceAgainst, hexColor, watchedAttributes]);

  if (conflictingAttributes.length === 0) {
    return null;
  }

  return (
    <FormWarning>
      <IconAlertCircleStyled />
      <Paragraph size='sm'>
        Warning: Not{' '}
        <a
          href='https://www.w3.org/WAI/WCAG21/quickref/?versions=2.0#contrast-minimum'
          target='_blank'
          rel='noreferrer'
        >
          WCAG compliant.
        </a>
        {conflictingAttributeLabels.length > 0 &&
          ` Not enough contrast against ${createPostfix(conflictingAttributeLabels)}.`}
      </Paragraph>
    </FormWarning>
  );
};

export default WCAGWarning;
